import { z } from 'zod';
import {
  LifecycleStateFilterSchema,
  OptionalBooleanQueryParamSchema,
  PaginationOrderDirectionEnum,
} from '@terra/shared-dto-types';
import {
  AiApplicationComponentsResponseSchema,
  AiApplicationInterfaceTypeSchema,
  AiComponentSchema,
  AiComponentTypeSchema,
} from './ai-applications.schema';

export const AI_COMPONENT_LIST_SEGMENTS = ['agents', 'components'] as const;

export const AI_COMPONENT_LIST_SORT_FIELDS = [
  'name',
  'type',
  'role',
  'createdAt',
  'signals',
  'findings',
] as const;

export const AI_COMPONENT_LIST_GROUP_BY_FIELDS = [
  'type',
  'role',
  'isInternal',
] as const;

export const AI_COMPONENT_LIST_MAX_LIMIT = 200;

export const AiComponentListSegmentSchema = z.enum(AI_COMPONENT_LIST_SEGMENTS);

export const AiComponentListSortFieldSchema = z.enum(
  AI_COMPONENT_LIST_SORT_FIELDS,
);

export const AiComponentListGroupByFieldSchema = z.enum(
  AI_COMPONENT_LIST_GROUP_BY_FIELDS,
);

export const AiComponentListOrderBySchema = z.object({
  field: AiComponentListSortFieldSchema,
  order: PaginationOrderDirectionEnum,
});

export const AiComponentListGroupSchema = z.object({
  key: z.string().nullable(),
  count: z.int().nonnegative(),
});

export const AiComponentListSegmentCountsSchema = z.object({
  agents: z.int().nonnegative(),
  components: z.int().nonnegative(),
});

const AiComponentTypesQueryParamSchema = z
  .union([AiComponentTypeSchema, z.array(AiComponentTypeSchema)])
  .transform((value) => (Array.isArray(value) ? value : [value]));

export const AiComponentsListQuerySchema = z.object({
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
  segment: AiComponentListSegmentSchema.optional(),
  search: z.string().trim().min(1).max(200).optional(),
  types: AiComponentTypesQueryParamSchema.optional(),
  isInternal: OptionalBooleanQueryParamSchema,
  role: z.string().trim().min(1).max(200).optional(),
  interfaceType: AiApplicationInterfaceTypeSchema.optional(),
  orderBy: AiComponentListOrderBySchema.array().max(1).optional(),
  groupBy: AiComponentListGroupByFieldSchema.optional(),
  skip: z.coerce.number().int().min(0).optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(AI_COMPONENT_LIST_MAX_LIMIT)
    .optional(),
});

export const AiApplicationComponentsListResponseSchema =
  AiApplicationComponentsResponseSchema.extend({
    totalCount: z.int().nonnegative().optional(),
    groups: AiComponentListGroupSchema.array().optional(),
    segmentCounts: AiComponentListSegmentCountsSchema.optional(),
    relatedComponents: z.array(AiComponentSchema).optional(),
  });

export type AiComponentListSegment = z.infer<
  typeof AiComponentListSegmentSchema
>;
export type AiComponentListSortField = z.infer<
  typeof AiComponentListSortFieldSchema
>;
export type AiComponentListGroupByField = z.infer<
  typeof AiComponentListGroupByFieldSchema
>;
export type AiComponentListOrderByDto = z.infer<
  typeof AiComponentListOrderBySchema
>;
export type AiComponentListGroupDto = z.infer<
  typeof AiComponentListGroupSchema
>;
export type AiComponentListSegmentCountsDto = z.infer<
  typeof AiComponentListSegmentCountsSchema
>;
export type AiComponentsListQueryDto = z.infer<
  typeof AiComponentsListQuerySchema
>;
export type AiComponentsListQueryInput = z.input<
  typeof AiComponentsListQuerySchema
>;
export type AiApplicationComponentsListResponseDto = z.infer<
  typeof AiApplicationComponentsListResponseSchema
>;
